import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Spinner } from '../../components/ui/Spinner';
import { Modal } from '../../components/ui/Modal';
import { DecimalInput } from '../../components/ui/DecimalInput';
import { fmt } from '../../lib/formatters';
import { coloresDePrecios, nombreConColor, normalizarColor, precioParaColor } from '../../../shared/precio-color';
import { supabase } from '../../lib/supabase';

const CLIENTE_DEFAULT = 'GAME';

// ─────────────────────────────────────────────────────────────────────
// Tipos
// ─────────────────────────────────────────────────────────────────────

type Categoria = {
  id: string;
  nombre: string;
  /** Coste transporte unitario calculado por el backend (mismo patrón
   *  que en subcategorias). Se usa como fallback final cuando no hay
   *  uds_40HQ del producto ni subcategoría con dato. */
  coste_transporte_actual_eur?: number | string | null;
};
type Subcategoria = {
  id: string;
  nombre: string;
  categoria_id: string;
  units_contenedor_40hq: number | string | null;
  arancel_pct: number | string | null;
  /** Coste transporte unitario calculado dinámicamente por el backend
   *  (precio_contenedor_eur / units_contenedor_40hq) o coste_transporte_base. */
  coste_transporte_actual_eur: number | string | null;
};
type DetalleFull = {
  id: string;
  nombre: string;
  subcategoria_id: string;
  activo: boolean;
  /** Valores efectivos (override del detalle > subcategoría) que devuelve
   *  /admin/detalles/listado/full. Se usan como override sobre la subcat. */
  arancel_pct_efectivo: number | string | null;
  units_contenedor_40hq_efectivo: number | string | null;
  coste_transporte_actual_eur: number | string | null;
};
type Gama = { id: string; nombre: string };
type Proveedor = { id: string; nombre: string; foc_pct: number | string | null };
type Cliente = { id: string; codigo: string; aplica_costes_new_product?: boolean };
type Marca = { id: string; cliente_id: string; codigo: string; nombre: string };

type Condicion = {
  id: string;
  marca_id: string;
  gama_id: string | null;
  categoria_id: string | null;
  subcategoria_id: string | null;
  margen_pct: number | string | null;
  rappel_pct: number | string | null;
  marketing_pct: number | string | null;
  foc_target_pct: number | string | null;
  comision_pm_pct: number | string | null;
  financiero_pct: number | string | null;
  garantiza_margen_minimo?: boolean | null;
};

type SubcategoriasFull = {
  data: Subcategoria[];
  contexto: { precio_contenedor_eur: number };
};

type FxStatus = {
  valor: number | null;
  margen_seguridad: number;
  valor_con_margen: number | null;
};

type Opcion = {
  uid: string;
  descripcion: string;
  importe_usd: number;
  incluido: boolean;
};

type CalcInputs = {
  unidades: number;
  coste_usd: number;
  foc_extra_pct: number;
  /** FOC % que ofrece el proveedor — informativo, no entra en el cálculo. */
  foc_proveedor_pct: number;
  aranceles_pct: number;
  cambio_usd_eur: number;
  coste_contenedor_eur: number;
  unidades_40hq: number;
  coste_transporte_unidad_eur: number;
  margen_approx_pct: number;
  comision_pm_pct: number;
  rappel_game_pct: number;
  mkt_game_pct: number;
  financiero_pct: number;
  coste_video_eur: number;
  coste_traduccion_eur: number;
  provisiones_eur: number;
  bolsa_eur: number;
};

type CalcResult = CalcInputs & {
  cdpe_eur: number;
  coste_basico_eur: number;
  coste_new_product_eur: number;
  coste_real_eur: number;
};

const EMPTY_INPUTS: CalcInputs = {
  unidades: 1000,
  coste_usd: 0,
  foc_extra_pct: 0,
  foc_proveedor_pct: 0,
  aranceles_pct: 0,
  cambio_usd_eur: 0.92,
  coste_contenedor_eur: 0,
  unidades_40hq: 0,
  coste_transporte_unidad_eur: 0,
  margen_approx_pct: 0,
  comision_pm_pct: 0,
  rappel_game_pct: 0,
  mkt_game_pct: 0,
  financiero_pct: 0,
  coste_video_eur: 0,
  coste_traduccion_eur: 0,
  provisiones_eur: 0,
  bolsa_eur: 0,
};

const num = (v: number | string | null | undefined): number => {
  if (v == null || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const newOpcionUid = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const opcionVacia = (): Opcion => ({ uid: newOpcionUid(), descripcion: '', importe_usd: 0, incluido: false });

// ─────────────────────────────────────────────────────────────────────
// Componente
// ─────────────────────────────────────────────────────────────────────

/**
 * Si `embebida` está set, la calculadora se monta dentro de una ficha
 * de producto: la cabecera "Producto a cotizar" y el listado de
 * "Últimas cotizaciones" se ocultan (la primera porque los IDs vienen
 * del producto y son inmutables; el segundo porque ProductoDetalle
 * tiene su propio histórico filtrado por producto). Al guardar se
 * envía `producto_id` para asociar la cotización al producto.
 */
type EmbebidaProps = {
  producto: {
    id: string;
    gama_id: string | null;
    categoria_id: string | null;
    subcategoria_id?: string | null;
    proveedor_id: string | null;
    ref_proveedor: string | null;
  };
  /** Callback opcional para invalidar queries del producto tras guardar. */
  onSaved?: () => void;
};

export function GameCalculadora({ embebida, clienteCodigo = CLIENTE_DEFAULT }: { embebida?: EmbebidaProps; clienteCodigo?: string } = {}) {
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const ppIdParam = searchParams.get('pp');
  const marcaParam = searchParams.get('marca');
  // Color de la cotización (?color=). Si el producto de proveedor tiene precios
  // por color y no viene en la URL, se pregunta antes de precargar nada.
  // undefined = aún sin decidir; null = sin color (precio general).
  const [colorElegido, setColorElegido] = useState<string | null | undefined>(() => normalizarColor(searchParams.get('color')) ?? undefined);

  // ── Catálogos / parámetros globales
  const gamas = useQuery({ queryKey: ['list', '/admin/gamas'], queryFn: () => api.get<{ data: Gama[] }>('/admin/gamas') });
  const categorias = useQuery({
    queryKey: ['list', '/admin/categorias/listado/full'],
    queryFn: () => api.get<{ data: Categoria[] }>('/admin/categorias/listado/full'),
  });
  const subcats = useQuery({ queryKey: ['list', '/admin/subcategorias/listado/full'], queryFn: () => api.get<SubcategoriasFull>('/admin/subcategorias/listado/full') });
  const detalles = useQuery({ queryKey: ['list', '/admin/detalles/listado/full'], queryFn: () => api.get<{ data: DetalleFull[] }>('/admin/detalles/listado/full') });
  const proveedores = useQuery({ queryKey: ['list', '/proveedores'], queryFn: () => api.get<{ data: Proveedor[] }>('/proveedores') });
  const clientes = useQuery({ queryKey: ['list', '/admin/clientes'], queryFn: () => api.get<{ data: Cliente[] }>('/admin/clientes') });
  const marcas = useQuery({ queryKey: ['list', '/admin/marcas'], queryFn: () => api.get<{ data: Marca[] }>('/admin/marcas') });
  const condiciones = useQuery({
    queryKey: ['condiciones-comerciales', clienteCodigo, 'calc'],
    queryFn: () => api.get<{ data: Condicion[] }>(`/admin/condiciones-comerciales/listado/full?cliente_codigo=${clienteCodigo}`),
  });
  const fx = useQuery<FxStatus>({
    queryKey: ['fx-status-calc'],
    queryFn: () => api.get<FxStatus>('/parametros/_fx/status'),
  });
  const paramContenedor = useQuery({ queryKey: ['param', 'precio_contenedor_eur'], queryFn: () => api.get<{ valor: string }>('/parametros/precio_contenedor_eur') });
  const paramVideo = useQuery({ queryKey: ['param', 'video_nuevos_productos_eur'], queryFn: () => api.get<{ valor: string }>('/parametros/video_nuevos_productos_eur') });
  const paramTrad = useQuery({ queryKey: ['param', 'traduccion_nuevo_producto_eur'], queryFn: () => api.get<{ valor: string }>('/parametros/traduccion_nuevo_producto_eur') });
  const paramIva = useQuery({ queryKey: ['param', 'iva_general_pct'], queryFn: () => api.get<{ valor: string }>('/parametros/iva_general_pct') });

  // ── Listado de cotizaciones recientes (debajo). Solo en modo standalone.
  const recientes = useQuery({
    queryKey: ['cotizaciones', 'recientes', clienteCodigo],
    queryFn: () => api.get<{ data: CotizacionRow[] }>(`/precio/cotizaciones/recientes?cliente_codigo=${clienteCodigo}&limit=30`),
    enabled: !embebida,
  });

  // ── Pre-carga desde producto de proveedor (vía ?pp=<id> en la URL)
  type PPPrecio = { fecha: string; color: string | null; tramo1_precio_usd: number | string | null };
  type PPFull = {
    id: string; nombre: string; ref_proveedor: string; main_specs: string | null;
    proveedor_id: string | null; gama_id: string | null; categoria_id: string | null;
    subcategoria_id: string | null; detalle_id: string | null; moq: number | null; foc_pct: number | null;
    units_40hq: number | null; precios: PPPrecio[];
    envio_tipo: 'FOB' | 'EXW' | null; packaging_included: boolean | null;
    // Dimensiones para derivar el coste de envío propio del producto.
    ctn_largo_cm: number | string | null; ctn_ancho_cm: number | string | null; ctn_alto_cm: number | string | null;
    cbm_ctn: number | string | null; carton_box_pcs: number | string | null;
    unit_box_largo_cm: number | string | null; unit_box_ancho_cm: number | string | null; unit_box_alto_cm: number | string | null;
  };
  const ppQuery = useQuery({
    queryKey: ['producto-proveedor', ppIdParam, 'full'],
    queryFn: () => api.get<PPFull>(`/productos-proveedor/${ppIdParam}/full`),
    enabled: !!ppIdParam && !embebida,
  });
  const [ppApplied, setPpApplied] = useState(false);
  const coloresPP = useMemo(() => coloresDePrecios(ppQuery.data?.precios ?? []), [ppQuery.data]);
  const preguntarColor = !embebida && !ppApplied && coloresPP.length > 0 && colorElegido === undefined;
  // Foto existente del producto (imagen principal) para heredarla en la zona
  // de foto cuando se cotiza desde un producto de proveedor (?pp=).
  const ppArchivos = useQuery({
    queryKey: ['pp-archivos-img', ppIdParam],
    queryFn: () => api.get<{ data: { tipo: string; url: string | null }[] }>(`/productos-proveedor/${ppIdParam}/archivos`),
    enabled: !!ppIdParam && !embebida,
  });
  const fotoHeredadaUrl = useMemo(() => {
    const img = (ppArchivos.data?.data ?? []).find((a) => a.tipo === 'imagen_principal');
    return img?.url ?? null;
  }, [ppArchivos.data]);

  // ── Producto ya existente (modo embebida): último precio de proveedor + MOQ
  // para precargar el coste de fábrica. Decisión CEO 2026-06-16.
  type EmbebidaPrecio = {
    coste_usd: number | null; moq: number | null; foc_pct: number | null; fecha: string | null;
    units_40hq: number | null; cbm_ctn: number | string | null; carton_box_pcs: number | null;
    ctn_largo_cm: number | null; ctn_ancho_cm: number | null; ctn_alto_cm: number | null;
    unit_box_largo_cm: number | null; unit_box_ancho_cm: number | null; unit_box_alto_cm: number | null;
  };
  const embebidaPrecio = useQuery({
    queryKey: ['producto', embebida?.producto.id, 'precio-proveedor'],
    queryFn: () => api.get<EmbebidaPrecio>(`/productos/${embebida!.producto.id}/precio-proveedor`),
    enabled: !!embebida?.producto.id,
  });
  const [embebidaPrecioApplied, setEmbebidaPrecioApplied] = useState(false);

  // ── Estado del formulario. En modo embebida arrancamos pre-poblados
  //    desde el producto; las selects de cabecera no se renderizan, así
  //    que estos IDs quedan fijos para todo el ciclo de vida del componente.
  const [gamaId, setGamaId] = useState(embebida?.producto.gama_id ?? '');
  const [categoriaId, setCategoriaId] = useState(embebida?.producto.categoria_id ?? '');
  const [subcategoriaId, setSubcategoriaId] = useState(embebida?.producto.subcategoria_id ?? '');
  const [detalleId, setDetalleId] = useState('');
  const [proveedorId, setProveedorId] = useState(embebida?.producto.proveedor_id ?? '');
  const [refProveedor, setRefProveedor] = useState(embebida?.producto.ref_proveedor ?? '');
  const [nombrePropuesto, setNombrePropuesto] = useState('');
  const [mainSpecs, setMainSpecs] = useState('');
  // Términos de compra de ESTA cotización (antes vivían en el producto de
  // proveedor; decisión CEO 2026-06-03: son datos de la cotización).
  const [envioTipo, setEnvioTipo] = useState<'FOB' | 'EXW'>('FOB');
  const [packagingIncluded, setPackagingIncluded] = useState(false);

  const [inputs, setInputs] = useState<CalcInputs>(EMPTY_INPUTS);
  const [costeFinal, setCosteFinal] = useState<number>(0);
  const [costeFinalManual, setCosteFinalManual] = useState(false);
  const [pvprIva, setPvprIva] = useState<number>(0);
  // Garantizar margen mínimo Approx (sobre venta neta). Default lo
  // copia de la condición comercial activa; el CEO puede des/marcar.
  const [garantizarMargen, setGarantizarMargen] = useState<boolean>(false);
  // FOC target de la condición comercial activa. El F.O.C. extra por defecto es
  // lo que falta para llegar a este target descontando el FOC del proveedor.
  const [focTargetPct, setFocTargetPct] = useState<number>(0);

  // ── Opciones (extras del fabricante en USD)
  const [opciones, setOpciones] = useState<Opcion[]>([opcionVacia(), opcionVacia(), opcionVacia()]);

  // ── Aplicar datos del producto proveedor (una sola vez al cargar)
  useEffect(() => {
    if (ppApplied || !ppQuery.data || embebida) return;
    const pp = ppQuery.data;
    // Con precios por color, espera a que se elija el color (modal).
    if (coloresDePrecios(pp.precios ?? []).length > 0 && colorElegido === undefined) return;
    const color = colorElegido ?? null;
    if (pp.gama_id) setGamaId(pp.gama_id);
    if (pp.categoria_id) setCategoriaId(pp.categoria_id);
    if (pp.subcategoria_id) setSubcategoriaId(pp.subcategoria_id);
    if (pp.detalle_id) setDetalleId(pp.detalle_id);
    if (pp.proveedor_id) setProveedorId(pp.proveedor_id);
    setRefProveedor(pp.ref_proveedor ?? '');
    setNombrePropuesto(nombreConColor(pp.nombre ?? '', color));
    setMainSpecs(pp.main_specs ?? '');
    if (pp.envio_tipo) setEnvioTipo(pp.envio_tipo);
    setPackagingIncluded(Boolean(pp.packaging_included));
    const ultimoPrecio = precioParaColor(pp.precios ?? [], color)?.tramo1_precio_usd;
    setInputs((s) => ({
      ...s,
      coste_usd: ultimoPrecio != null ? num(ultimoPrecio) : s.coste_usd,
      unidades: pp.moq ? num(pp.moq) : s.unidades,
      // unidades_40hq lo resuelve units40hqInfo (producto > detalle > subcat).
    }));
    setPpApplied(true);
  }, [ppQuery.data, ppApplied, embebida, colorElegido]);

  // ── (embebida) Aplicar el último precio de proveedor + MOQ al coste de
  //    fábrica de un producto ya existente. Una sola vez.
  useEffect(() => {
    if (!embebida || embebidaPrecioApplied || !embebidaPrecio.data) return;
    const d = embebidaPrecio.data;
    if (d.coste_usd == null && d.moq == null) { setEmbebidaPrecioApplied(true); return; }
    setInputs((s) => ({
      ...s,
      coste_usd: d.coste_usd != null ? d.coste_usd : s.coste_usd,
      unidades: d.moq != null ? d.moq : s.unidades,
    }));
    setEmbebidaPrecioApplied(true);
  }, [embebida, embebidaPrecio.data, embebidaPrecioApplied]);

  // F.O.C. Incluido Proveedor: hereda las condiciones del proveedor
  // seleccionado (proveedores.foc_pct). Decisión CEO 2026-06-03. Se aplica al
  // elegir proveedor (y al cargar la lista); si el CEO lo edita a mano después,
  // se respeta hasta que cambie de proveedor.
  useEffect(() => {
    if (!proveedorId) return;
    const prov = proveedores.data?.data.find((x) => x.id === proveedorId);
    if (prov && prov.foc_pct != null && prov.foc_pct !== '') {
      const provPct = Number(prov.foc_pct);
      setInputs((s) => ({
        ...s,
        foc_proveedor_pct: provPct,
        // F.O.C. extra por defecto = lo que falta para llegar al FOC target,
        // descontando el FOC ya incluido por el proveedor. Decisión CEO 2026-06-16.
        foc_extra_pct: Math.max(0, Number((focTargetPct - provPct).toFixed(4))),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proveedorId, proveedores.data, focTargetPct]);

  const opcionesIncluidasTotal = useMemo(
    () => opciones.reduce((acc, o) => acc + (o.incluido ? num(o.importe_usd) : 0), 0),
    [opciones],
  );
  const costeMasOpciones = useMemo(() => inputs.coste_usd + opcionesIncluidasTotal, [inputs.coste_usd, opcionesIncluidasTotal]);

  // ── Cliente del cliente_codigo + flag aplica_costes_new_product
  // (se calculan aquí arriba porque varios useEffect dependen de ellos).
  const clienteActual = useMemo<Cliente | null>(
    () => (clientes.data?.data ?? []).find((c) => c.codigo === clienteCodigo) ?? null,
    [clientes.data, clienteCodigo],
  );
  const aplicaNewProduct = clienteActual?.aplica_costes_new_product !== false;

  // ── Cargar defaults una vez tengamos parámetros globales + FX
  useEffect(() => {
    if (!fx.data?.valor_con_margen) return;
    setInputs((s) => ({
      ...s,
      cambio_usd_eur: Number(fx.data!.valor_con_margen!.toFixed(6)),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fx.data?.valor_con_margen]);

  useEffect(() => {
    if (!paramContenedor.data?.valor) return;
    setInputs((s) => ({ ...s, coste_contenedor_eur: num(paramContenedor.data!.valor) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramContenedor.data?.valor]);

  useEffect(() => {
    // Si el cliente no aplica costes "new product" (vídeo + traducción)
    // el default es 0 (lo que ya hay en EMPTY_INPUTS). Solo cargamos el
    // parámetro global cuando el cliente sí los aplica. El CEO puede
    // editarlos manualmente desde la UI en cualquier caso.
    // En modo embebida (producto YA existente) el coste de vídeo/traducción es 0
    // por defecto: solo aplican a productos nuevos. Decisión CEO 2026-06-16.
    if (!aplicaNewProduct || embebida) return;
    if (paramVideo.data?.valor != null) {
      setInputs((s) => ({ ...s, coste_video_eur: num(paramVideo.data!.valor) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramVideo.data?.valor, aplicaNewProduct]);

  useEffect(() => {
    if (!aplicaNewProduct || embebida) return;
    if (paramTrad.data?.valor != null) {
      setInputs((s) => ({ ...s, coste_traduccion_eur: num(paramTrad.data!.valor) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramTrad.data?.valor, aplicaNewProduct]);

  // ── Subcat / detalle seleccionados. El detalle (4ª capa) hace override
  //    del arancel y units/40HQ sobre la subcategoría, igual que el backend
  //    (jerarquía detalle > subcategoría).
  const subcatSel = useMemo(() => subcats.data?.data.find((s) => s.id === subcategoriaId) ?? null, [subcategoriaId, subcats.data]);
  const detalleSel = useMemo(() => detalles.data?.data.find((d) => d.id === detalleId) ?? null, [detalleId, detalles.data]);
  useEffect(() => {
    if (!subcatSel) return;
    // Detalle gana sobre subcat; los campos *_efectivo ya coalescen detalle→subcat.
    // Las uds/40HQ las resuelve `units40hqInfo` (producto > detalle > subcat).
    setInputs((s) => ({
      ...s,
      aranceles_pct: num(detalleSel?.arancel_pct_efectivo ?? subcatSel.arancel_pct),
    }));
  }, [subcatSel, detalleSel]);

  // ── Subcategorías filtradas por categoría
  const subcategoriasFiltradas = useMemo(() => {
    const all = subcats.data?.data ?? [];
    if (!categoriaId) return all;
    return all.filter((s) => s.categoria_id === categoriaId);
  }, [categoriaId, subcats.data]);
  // ── Detalles filtrados por subcategoría (solo activos)
  const detallesFiltrados = useMemo(() => {
    const all = (detalles.data?.data ?? []).filter((d) => d.activo);
    if (!subcategoriaId) return all;
    return all.filter((d) => d.subcategoria_id === subcategoriaId);
  }, [subcategoriaId, detalles.data]);

  // ── Marcas del cliente actual (filtramos del catálogo global)
  const marcasDelCliente = useMemo<Marca[]>(() => {
    if (!clienteActual) return [];
    return (marcas.data?.data ?? []).filter((m) => m.cliente_id === clienteActual.id);
  }, [clienteActual, marcas.data]);

  // Default por cliente: GAME → marca codigo=GAME; TICNOVA → marca NETWAY
  // (por codigo o por nombre, en ese orden). Si no encuentra ninguna,
  // primera marca del cliente.
  const marcaDefault = useMemo<string>(() => {
    if (marcasDelCliente.length === 0) return '';
    const buscar = (pred: (m: Marca) => boolean) => marcasDelCliente.find(pred)?.id ?? '';
    if (clienteCodigo === 'GAME') {
      return buscar((m) => m.codigo === 'GAME') || marcasDelCliente[0].id;
    }
    if (clienteCodigo === 'TICNOVA') {
      return buscar((m) => m.codigo === 'NETWAY')
        || buscar((m) => (m.nombre ?? '').toUpperCase() === 'NETWAY')
        || marcasDelCliente[0].id;
    }
    return marcasDelCliente[0].id;
  }, [marcasDelCliente, clienteCodigo]);

  const [marcaId, setMarcaId] = useState<string>('');
  // Una vez que las marcas del cliente están disponibles, aplica primero
  // el parámetro de URL `?marca=<id>` si llega del modal de "Cotizar"; si
  // no, el default por cliente. Cambios posteriores del usuario se respetan.
  useEffect(() => {
    if (marcaId) return;
    if (marcaParam && marcasDelCliente.some((m) => m.id === marcaParam)) {
      setMarcaId(marcaParam);
      return;
    }
    if (marcaDefault) setMarcaId(marcaDefault);
  }, [marcaDefault, marcaId, marcaParam, marcasDelCliente]);

  // ── Cargar condiciones (margen, comisión, rappel, mkt, financiero, FOC)
  // según marca / gama / subcat. Match más específico = más prioritario.
  useEffect(() => {
    if (!marcaId) return;
    const lista = condiciones.data?.data ?? [];
    const aplica = (c: Condicion): boolean => c.marca_id === marcaId;
    const score = (c: Condicion): number => {
      let s = 0;
      if (c.subcategoria_id === subcategoriaId) s += 8;
      else if (c.subcategoria_id !== null) return -1;
      if (c.categoria_id === categoriaId) s += 4;
      else if (c.categoria_id !== null) return -1;
      if (c.gama_id === gamaId) s += 2;
      else if (c.gama_id !== null) return -1;
      return s;
    };
    const candidatas = lista.filter(aplica).map((c) => ({ c, s: score(c) })).filter((x) => x.s >= 0);
    candidatas.sort((a, b) => b.s - a.s);
    const cc = candidatas[0]?.c;
    if (!cc) return;
    const focTarget = num(cc.foc_target_pct);
    setFocTargetPct(focTarget);
    setInputs((s) => ({
      ...s,
      margen_approx_pct: num(cc.margen_pct),
      comision_pm_pct: num(cc.comision_pm_pct),
      rappel_game_pct: num(cc.rappel_pct),
      mkt_game_pct: num(cc.marketing_pct),
      financiero_pct: num(cc.financiero_pct),
      // F.O.C. extra por defecto = FOC target menos el FOC del proveedor.
      foc_extra_pct: Math.max(0, Number((focTarget - s.foc_proveedor_pct).toFixed(4))),
    }));
    // Copia el flag de garantía de la condición. El CEO podrá
    // desmarcarlo en la UI; cambios de gama/cat/sub posteriores vuelven
    // a copiarlo de la condición correspondiente.
    setGarantizarMargen(Boolean(cc.garantiza_margen_minimo));
  }, [marcaId, condiciones.data, gamaId, categoriaId, subcategoriaId]);

  // ── Coste transporte unidad: jerarquía en cascada (producto > subcat
  // > categoría). Solo aplica si el usuario no editó manualmente:
  //   1. Si tenemos uds/40HQ y precio contenedor → reparto directo
  //      (precio_contenedor / uds_40HQ).
  //   2. Sino, coste_transporte_actual_eur de la subcategoría seleccionada
  //      (lo precalcula el backend con units_contenedor_40hq o
  //      coste_transporte_base_eur escalado).
  //   3. Sino, mismo dato de la categoría — fallback final pedido por el
  //      CEO para que la calculadora muestre algo razonable cuando solo
  //      hay categoría asignada.
  const categoriaSel = useMemo(
    () => (categorias.data?.data ?? []).find((c) => c.id === categoriaId) ?? null,
    [categoriaId, categorias.data],
  );
  // Logística del producto: de la ficha de proveedor cargada por ?pp= (modo
  // standalone) o del producto ya existente (modo embebida, vía precio-proveedor).
  const ppLogistics = useMemo<Record<string, number | string | null> | null>(() => {
    if (embebida) return (embebidaPrecio.data as unknown as Record<string, number | string | null>) ?? null;
    return (ppQuery.data as unknown as Record<string, number | string | null>) ?? null;
  }, [embebida, embebidaPrecio.data, ppQuery.data]);

  // Uds/40HQ + ORIGEN del dato, con cascada: datos propios del producto
  // (uds/40HQ directo → CBM/CTN × uds/caja → volumen de caja) → detalle →
  // subcategoría. Es lo que se usa para el coste de transporte unitario.
  const units40hqInfo = useMemo<{ value: number | null; fuente: string | null }>(() => {
    const lg = ppLogistics;
    if (lg) {
      const u40 = num(lg.units_40hq);
      if (u40 > 0) return { value: u40, fuente: 'datos del producto (uds/40HQ propias)' };
      const cbmManual = num(lg.cbm_ctn);
      const ctnL = num(lg.ctn_largo_cm), ctnA = num(lg.ctn_ancho_cm), ctnH = num(lg.ctn_alto_cm);
      const cbmDeriv = ctnL > 0 && ctnA > 0 && ctnH > 0 ? (ctnL * ctnA * ctnH) / 1_000_000 : 0;
      const cbm = cbmManual > 0 ? cbmManual : cbmDeriv;
      const cartonPcs = num(lg.carton_box_pcs);
      if (cbm > 0 && cartonPcs > 0) return { value: Math.floor(67 / cbm) * cartonPcs, fuente: 'datos del producto (CBM/CTN × uds/caja)' };
      const ubL = num(lg.unit_box_largo_cm), ubA = num(lg.unit_box_ancho_cm), ubH = num(lg.unit_box_alto_cm);
      const m3 = ubL > 0 && ubA > 0 && ubH > 0 ? (ubL * ubA * ubH) / 1_000_000 : 0;
      if (m3 > 0) return { value: Math.floor(67 / m3), fuente: 'datos del producto (volumen caja unitaria)' };
    }
    if (detalleSel && num(detalleSel.units_contenedor_40hq_efectivo) > 0) {
      return { value: num(detalleSel.units_contenedor_40hq_efectivo), fuente: 'detalle' };
    }
    if (subcatSel && num(subcatSel.units_contenedor_40hq) > 0) {
      return { value: num(subcatSel.units_contenedor_40hq), fuente: 'subcategoría' };
    }
    return { value: null, fuente: null };
  }, [ppLogistics, detalleSel, subcatSel]);

  // Refleja las uds/40HQ resueltas en el campo (salvo edición manual).
  const [unidades40hqManual, setUnidades40hqManual] = useState(false);
  useEffect(() => {
    if (unidades40hqManual || units40hqInfo.value == null) return;
    setInputs((s) => (s.unidades_40hq === units40hqInfo.value ? s : { ...s, unidades_40hq: units40hqInfo.value as number }));
  }, [units40hqInfo, unidades40hqManual]);

  const [transporteManual, setTransporteManual] = useState(false);
  // Origen del coste de transporte unitario, mostrado bajo el campo.
  const transporteFuente = useMemo<string>(() => {
    if (transporteManual) return 'Editado manual';
    if (units40hqInfo.value != null && units40hqInfo.value > 0 && inputs.coste_contenedor_eur > 0) {
      return `Auto = contenedor / uds 40HQ · uds 40HQ de ${units40hqInfo.fuente}`;
    }
    if (detalleSel?.coste_transporte_actual_eur != null) return 'Auto = coste de transporte del detalle';
    if (subcatSel?.coste_transporte_actual_eur != null) return 'Auto = coste de transporte de la subcategoría';
    if (categoriaSel?.coste_transporte_actual_eur != null) return 'Auto = coste de transporte de la categoría (fallback)';
    return 'Sin auto-cálculo';
  }, [transporteManual, units40hqInfo, inputs.coste_contenedor_eur, detalleSel, subcatSel, categoriaSel]);

  useEffect(() => {
    if (transporteManual) return;
    let t: number | null = null;
    if (units40hqInfo.value != null && units40hqInfo.value > 0 && inputs.coste_contenedor_eur > 0) {
      // Datos del producto (o, en su defecto, detalle/subcat) → reparto directo.
      t = inputs.coste_contenedor_eur / units40hqInfo.value;
    } else if (detalleSel?.coste_transporte_actual_eur != null) {
      t = num(detalleSel.coste_transporte_actual_eur);
    } else if (subcatSel?.coste_transporte_actual_eur != null) {
      t = num(subcatSel.coste_transporte_actual_eur);
    } else if (categoriaSel?.coste_transporte_actual_eur != null) {
      t = num(categoriaSel.coste_transporte_actual_eur);
    }
    if (t != null) {
      setInputs((s) => ({ ...s, coste_transporte_unidad_eur: Number(t.toFixed(2)) }));
    }
  }, [inputs.coste_contenedor_eur, units40hqInfo, detalleSel, subcatSel, categoriaSel, transporteManual]);

  // ── Cálculo en vivo (debounce 200 ms). Usamos coste_usd + opciones.
  const [result, setResult] = useState<CalcResult | null>(null);
  const calc = useMutation({
    mutationFn: (body: CalcInputs) => api.post<CalcResult>('/precio/calcular-completo', body),
    onSuccess: (r) => {
      setResult(r);
      if (!costeFinalManual) setCosteFinal(Number(r.coste_real_eur.toFixed(2)));
    },
  });

  // ── Precio mínimo para garantizar el margen Approx objetivo
  // (sobre venta neta). Si está activada la garantía y el coste final
  // base quedaría por debajo, subimos a este mínimo.
  const precioMinGarantizado = useMemo<number | null>(() => {
    if (!garantizarMargen || !result) return null;
    const margenObj = inputs.margen_approx_pct;
    if (margenObj <= 0 || margenObj >= 100) return null;
    const descuento = (inputs.rappel_game_pct + inputs.comision_pm_pct + inputs.mkt_game_pct) / 100;
    if (descuento >= 1) return null;
    const extras = inputs.unidades > 0
      ? (inputs.coste_video_eur + inputs.coste_traduccion_eur) / inputs.unidades
      : 0;
    const ventaNetaMin = result.cdpe_eur / (1 - margenObj / 100);
    return (ventaNetaMin + extras) / (1 - descuento);
  }, [garantizarMargen, result, inputs.margen_approx_pct, inputs.rappel_game_pct,
      inputs.comision_pm_pct, inputs.mkt_game_pct, inputs.coste_video_eur,
      inputs.coste_traduccion_eur, inputs.unidades]);
  useEffect(() => {
    if (!garantizarMargen || costeFinalManual || precioMinGarantizado == null) return;
    if (precioMinGarantizado > costeFinal) {
      // CEIL al céntimo → margen ≥ objetivo (toFixed redondeaba a la baja).
      setCosteFinal(Math.ceil(precioMinGarantizado * 100) / 100);
    }
  }, [precioMinGarantizado, garantizarMargen, costeFinalManual, costeFinal]);
  useEffect(() => {
    const bodyParaCalc: CalcInputs = { ...inputs, coste_usd: costeMasOpciones };
    const t = setTimeout(() => calc.mutate(bodyParaCalc), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputs, costeMasOpciones]);

  // ── Margen final que obtiene GAME = (pvpSinIva - costeFinal) / pvpSinIva
  const ivaPct = num(paramIva.data?.valor ?? 21);
  const pvprSinIva = pvprIva > 0 && ivaPct > -100 ? pvprIva / (1 + ivaPct / 100) : null;
  const margenGame = pvprSinIva != null && pvprSinIva > 0 && costeFinal > 0
    ? ((pvprSinIva - costeFinal) / pvprSinIva) * 100
    : null;

  // ── Tabla informativa: PVP con IVA según margen GAME deseado.
  const informativos = useMemo(() => {
    if (!costeFinal || costeFinal <= 0) return [];
    const margenes = [60, 50, 40, 30, 20, 10];
    return margenes.map((m) => {
      const pvSinIva = costeFinal / (1 - m / 100);
      const pvIva = pvSinIva * (1 + ivaPct / 100);
      return { margen: m, pv_iva: Number(pvIva.toFixed(2)) };
    });
  }, [costeFinal, ivaPct]);

  // ── Foto opcional (paste/drop/upload). Se asocia al producto_proveedor
  // que el backend auto-crea/encuentra al guardar la cotización.
  const [fotoFile, setFotoFile] = useState<File | null>(null);
  const [fotoPreview, setFotoPreview] = useState<string | null>(null);
  const fotoInputRef = useRef<HTMLInputElement | null>(null);
  const [fotoDragOver, setFotoDragOver] = useState(false);
  const aceptarFoto = (file: File | null) => {
    if (!file || !file.type.startsWith('image/')) return;
    setFotoFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setFotoPreview(typeof e.target?.result === 'string' ? e.target.result : null);
    reader.readAsDataURL(file);
  };
  // Paste global delimitado a la zona [data-foto-paste]: si el usuario hace
  // Ctrl+V con foco en el drop area, capturamos. Fuera de ella, no.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target || typeof target.closest !== 'function') return;
      if (!target.closest('[data-foto-paste="true"]')) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const it of Array.from(items)) {
        if (it.kind !== 'file') continue;
        const blob = it.getAsFile();
        if (blob && blob.type.startsWith('image/')) {
          e.preventDefault();
          aceptarFoto(blob);
          break;
        }
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, []);

  // ── Guardar
  const saveCotiz = useMutation({
    mutationFn: async () => {
      const created = await api.post<{ id: string; producto_proveedor_id: string | null }>(
        '/precio/cotizaciones/completa',
        {
          cliente_codigo: clienteCodigo,
          marca_id: marcaId || null,
          producto_id: embebida?.producto.id ?? null,
          subcategoria_id: subcategoriaId || null,
          proveedor_id: proveedorId || null,
          ref_proveedor: refProveedor || null,
          nombre_propuesto: nombrePropuesto || null,
          main_specs: mainSpecs.trim() || null,
          ...inputs,
          coste_usd: costeMasOpciones,
          coste_game_final_eur: costeFinal,
          pvpr_iva_eur: pvprIva > 0 ? pvprIva : null,
          iva_pct: ivaPct,
          parametros_snapshot: {
            gama_id: gamaId,
            categoria_id: categoriaId,
            subcategoria_id: subcategoriaId,
            detalle_id: detalleId || null,
            proveedor_id: proveedorId,
            envio_tipo: envioTipo,
            packaging_included: packagingIncluded,
            margen_game_calculado: margenGame,
            coste_fabrica_usd_base: inputs.coste_usd,
            opciones_incluidas_usd_total: opcionesIncluidasTotal,
            garantiza_margen_minimo: garantizarMargen,
            opciones: opciones.filter((o) => o.descripcion.trim() || o.importe_usd > 0).map((o) => ({
              descripcion: o.descripcion,
              importe_usd: o.importe_usd,
              incluido: o.incluido,
            })),
          },
        },
      );
      // Si el CEO añadió una foto y el backend creó/encontró un
      // producto_proveedor, sube la foto como imagen principal del catálogo
      // del proveedor. Si no hay PP (sin proveedor), avisamos al usuario.
      if (fotoFile) {
        if (!created.producto_proveedor_id) {
          // Lanzamos un soft-warning via throw para mostrar en la UI; la
          // cotización ya está guardada (no la deshacemos).
          throw new Error('Cotización guardada, pero la foto NO se subió: no hay producto del proveedor asociado (¿falta el proveedor?).');
        }
        const { data: sess } = await supabase.auth.getSession();
        const token = sess.session?.access_token;
        const fd = new FormData();
        fd.append('tipo', 'imagen_principal');
        fd.append('file', fotoFile);
        const resp = await fetch(`/api/productos-proveedor/${created.producto_proveedor_id}/archivos`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          body: fd,
        });
        if (!resp.ok) {
          const txt = await resp.text();
          throw new Error(`Cotización guardada, pero subir la foto falló (${resp.status}): ${txt}`);
        }
      }
      return created;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cotizaciones', 'recientes', clienteCodigo] });
      qc.invalidateQueries({ queryKey: ['catalogo'] });
      setFotoFile(null);
      setFotoPreview(null);
      embebida?.onSaved?.();
    },
  });

  const setI = <K extends keyof CalcInputs>(k: K, v: number) => setInputs((s) => ({ ...s, [k]: v }));

  if (categorias.isLoading || subcats.isLoading || gamas.isLoading) return <Spinner />;

  const cabeceraValida = !!categoriaId && !!subcategoriaId;

  return (
    <div>
      <Modal
        open={preguntarColor}
        onClose={() => setColorElegido(null)}
        title="¿En qué color la cotizas?"
        size="sm"
      >
        <p className="mb-3 text-sm text-slate-600">
          {ppQuery.data?.nombre} tiene precios por color. Se usa el precio de ese color y el color se añade al nombre del producto.
        </p>
        <div className="flex flex-col gap-2">
          {coloresPP.map((c) => {
            const pr = precioParaColor(ppQuery.data?.precios ?? [], c);
            return (
              <Button key={c} variant="secondary" onClick={() => setColorElegido(c)}>
                <span className="font-semibold">{c}</span>
                {pr?.tramo1_precio_usd != null && <span className="ml-2 font-mono text-xs text-slate-500">{fmt.usd(num(pr.tramo1_precio_usd))} · {fmt.date(pr.fecha)}</span>}
              </Button>
            );
          })}
          <button type="button" onClick={() => setColorElegido(null)} className="cursor-pointer text-xs text-slate-500 underline hover:text-slate-800">
            Sin color (precio general y nombre sin cambios)
          </button>
        </div>
      </Modal>
      {!embebida && (
        <PageHeader
          title={`Calculadora de precio ${clienteCodigo}`}
          subtitle="Calcula el coste real y el PVPr objetivo a partir del coste de fábrica."
        />
      )}

      {/* ── Cabecera: marca / gama / categoría / subcategoría / proveedor / ref ── */}
      {!embebida && (
        <Card title="Producto a cotizar">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-7">
            <LabeledField label="Marca *">
              <Select
                options={marcasDelCliente.map((m) => ({ value: m.id, label: m.nombre || m.codigo }))}
                value={marcaId}
                onChange={(e) => setMarcaId(e.target.value)}
                emptyLabel={marcasDelCliente.length === 0 ? '— sin marcas —' : '— elegir —'}
              />
            </LabeledField>
            <LabeledField label="Gama">
              <Select
                options={(gamas.data?.data ?? []).map((g) => ({ value: g.id, label: g.nombre }))}
                value={gamaId}
                onChange={(e) => setGamaId(e.target.value)}
                emptyLabel="— sin gama —"
              />
            </LabeledField>
            <LabeledField label="Categoría *">
              <Select
                options={(categorias.data?.data ?? []).map((c) => ({ value: c.id, label: c.nombre }))}
                value={categoriaId}
                onChange={(e) => { setCategoriaId(e.target.value); setSubcategoriaId(''); setDetalleId(''); }}
                emptyLabel="— elegir —"
              />
            </LabeledField>
            <LabeledField label="Subcategoría *">
              <Select
                options={subcategoriasFiltradas.map((s) => ({ value: s.id, label: s.nombre }))}
                value={subcategoriaId}
                onChange={(e) => { setSubcategoriaId(e.target.value); setDetalleId(''); }}
                emptyLabel="— elegir —"
                disabled={!categoriaId}
              />
            </LabeledField>
            <LabeledField label="Detalle">
              <Select
                options={detallesFiltrados.map((d) => ({ value: d.id, label: d.nombre }))}
                value={detalleId}
                onChange={(e) => setDetalleId(e.target.value)}
                emptyLabel="— sin detalle —"
                disabled={!subcategoriaId}
              />
            </LabeledField>
            <LabeledField label="Proveedor">
              <Select
                options={(proveedores.data?.data ?? []).map((p) => ({ value: p.id, label: p.nombre }))}
                value={proveedorId}
                onChange={(e) => setProveedorId(e.target.value)}
                emptyLabel="— sin asignar —"
              />
            </LabeledField>
            <LabeledField label="Ref. proveedor">
              <Input value={refProveedor} onChange={(e) => setRefProveedor(e.target.value)} />
            </LabeledField>
            <LabeledField label="Tipo de envío">
              <Select
                options={[{ value: 'FOB', label: 'FOB' }, { value: 'EXW', label: 'EXW' }]}
                value={envioTipo}
                emptyLabel={null}
                onChange={(e) => setEnvioTipo(e.target.value as 'FOB' | 'EXW')}
              />
            </LabeledField>
            <LabeledField label="Packaging incluido">
              <Select
                options={[{ value: 'true', label: 'Sí' }, { value: 'false', label: 'No' }]}
                value={String(packagingIncluded)}
                emptyLabel={null}
                onChange={(e) => setPackagingIncluded(e.target.value === 'true')}
              />
            </LabeledField>
            <div className="sm:col-span-2 lg:col-span-7">
              <LabeledField label="Nombre del producto y características principales (opcional)">
                <Input value={nombrePropuesto} onChange={(e) => setNombrePropuesto(e.target.value)} placeholder="Si lo dejas vacío, no se asocia a un producto concreto" />
              </LabeledField>
            </div>
            <div className="sm:col-span-2 lg:col-span-7">
              <LabeledField label="Main specs / resumen técnico (opcional) — se mostrará en la tabla del email al proveedor">
                <textarea
                  value={mainSpecs}
                  onChange={(e) => setMainSpecs(e.target.value)}
                  placeholder="Ej: HDMI 2.0, 144Hz, 1ms response time, ajustable en altura, USB-C 65W"
                  rows={2}
                  className="block w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
                />
              </LabeledField>
            </div>
            <div
              className="sm:col-span-2 lg:col-span-7"
              data-foto-paste="true"
              tabIndex={0}
              onDragOver={(e) => { e.preventDefault(); setFotoDragOver(true); }}
              onDragLeave={() => setFotoDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setFotoDragOver(false);
                aceptarFoto(e.dataTransfer.files?.[0] ?? null);
              }}
            >
              <LabeledField label="Foto (opcional) — se asociará como imagen principal del producto en el catálogo del proveedor">
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    onClick={() => fotoInputRef.current?.click()}
                    className={`flex h-28 w-28 cursor-pointer items-center justify-center overflow-hidden rounded-md border-2 border-dashed bg-white text-center text-xs transition outline-none ${
                      fotoDragOver
                        ? 'border-sky-500 bg-sky-50 text-sky-700'
                        : (fotoPreview || fotoHeredadaUrl)
                          ? 'border-slate-200'
                          : 'border-slate-300 text-slate-500 hover:border-slate-400 hover:text-slate-700'
                    }`}
                    title="Click, arrastra o pega con Ctrl+V (con esta zona enfocada)"
                  >
                    {fotoPreview
                      ? <img src={fotoPreview} alt="Foto" className="h-full w-full object-contain" />
                      : fotoHeredadaUrl
                        ? <img src={fotoHeredadaUrl} alt="Foto del producto" className="h-full w-full object-contain" />
                        : (fotoDragOver ? 'Suelta aquí' : <span>📷<br/>Click / pega / arrastra</span>)}
                  </button>
                  <div className="flex flex-col gap-1 text-xs text-slate-500">
                    <p>Acepta Ctrl+V con la zona enfocada, drag&amp;drop o click para elegir.</p>
                    {fotoHeredadaUrl && !fotoFile && (
                      <p className="text-slate-400">Foto heredada de la ficha del producto. Sube una nueva para reemplazarla.</p>
                    )}
                    {fotoFile && (
                      <p className="text-emerald-700">
                        ✓ <span className="font-mono">{fotoFile.name}</span> · {(fotoFile.size / 1024).toFixed(0)} KB
                      </p>
                    )}
                    {fotoFile && (
                      <button
                        type="button"
                        onClick={() => { setFotoFile(null); setFotoPreview(null); }}
                        className="self-start cursor-pointer text-rose-600 hover:underline"
                      >
                        Quitar foto
                      </button>
                    )}
                    {!proveedorId && fotoFile && (
                      <p className="text-amber-700">⚠ Necesitas seleccionar un proveedor para que la foto se asocie a un producto.</p>
                    )}
                  </div>
                </div>
                <input
                  ref={fotoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => { aceptarFoto(e.target.files?.[0] ?? null); if (fotoInputRef.current) fotoInputRef.current.value = ''; }}
                />
              </LabeledField>
            </div>
          </div>
        </Card>
      )}

      <div className={`grid gap-4 lg:grid-cols-3${embebida ? '' : ' mt-4'}`}>
        {/* ── Columna izquierda: PARÁMETROS APLICADOS (2 cols internas) ── */}
        <div className="lg:col-span-2 space-y-3">
          <Card
            title="Parámetros aplicados"
            description="Edita cualquier valor; el cálculo se actualiza en vivo. Acepta coma o punto como decimal."
            actions={
              <div
                className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs"
                title={fx.data ? `Real ${fmt.number(fx.data.valor)} + margen ${fmt.number(fx.data.margen_seguridad)}` : undefined}
              >
                <span className="font-medium uppercase tracking-wide text-slate-500">USD → EUR</span>
                <DecimalInput
                  value={inputs.cambio_usd_eur}
                  onChange={(v) => setI('cambio_usd_eur', v)}
                  ariaLabel="Cambio USD a EUR"
                  style={{ width: '5rem' }}
                  className="text-right font-mono text-xs"
                />
              </div>
            }
          >
            <div className="grid gap-x-5 gap-y-1 lg:grid-cols-3">
              <RowNum label="Unidades / MOQ" value={inputs.unidades} onChange={(v) => setI('unidades', v)} suffix="ud" />
              <RowNum
                label="Coste fábrica"
                value={inputs.coste_usd}
                onChange={(v) => setI('coste_usd', v)}
                suffix="USD"
              />
              <RowNum
                label="F.O.C. Incluido Proveedor"
                value={inputs.foc_proveedor_pct}
                onChange={(v) => setI('foc_proveedor_pct', v)}
                suffix="%"
                hint="Informativo — queda registrado en la cotización. No afecta al cálculo."
              />
              <RowNum
                label="Coste fábrica + Opciones"
                value={costeMasOpciones}
                onChange={() => { /* readonly */ }}
                suffix="USD"
                readOnly
                hint={opcionesIncluidasTotal > 0
                  ? `Incluye ${fmt.usd(opcionesIncluidasTotal)} de opciones marcadas`
                  : 'Marca opciones abajo para sumarlas'}
              />
              <RowNum label="F.O.C. extra" value={inputs.foc_extra_pct} onChange={(v) => setI('foc_extra_pct', v)} suffix="%" />
              <RowNum label="Aranceles" value={inputs.aranceles_pct} onChange={(v) => setI('aranceles_pct', v)} suffix="%" />
              <RowNum label="Coste contenedor" value={inputs.coste_contenedor_eur} onChange={(v) => setI('coste_contenedor_eur', v)} suffix="EUR" />
              <RowNum
                label="Uds / 40HQ"
                value={inputs.unidades_40hq}
                onChange={(v) => { setUnidades40hqManual(true); setI('unidades_40hq', v); }}
                suffix="ud"
                hint={unidades40hqManual
                  ? 'Editado manual'
                  : units40hqInfo.fuente
                    ? `Origen: ${units40hqInfo.fuente}`
                    : 'Sin datos de uds/40HQ (producto/detalle/subcategoría)'}
              />
              <RowNum
                label="Coste transporte ud"
                value={inputs.coste_transporte_unidad_eur}
                onChange={(v) => { setTransporteManual(true); setI('coste_transporte_unidad_eur', v); }}
                suffix="EUR"
                hint={transporteFuente}
              />
            </div>

            <Subtotal label="CDPe — Coste después de transporte estimado" value={result?.cdpe_eur} />

            <div className="mt-2 grid gap-x-5 gap-y-1 lg:grid-cols-3">
              <RowNum label="Margen Approx" value={inputs.margen_approx_pct} onChange={(v) => setI('margen_approx_pct', v)} suffix="%" />
              <RowNum label="Comisión PM" value={inputs.comision_pm_pct} onChange={(v) => setI('comision_pm_pct', v)} suffix="%" />
              <RowNum label="Rappel cliente" value={inputs.rappel_game_pct} onChange={(v) => setI('rappel_game_pct', v)} suffix="%" />
              <RowNum label="MKT cliente" value={inputs.mkt_game_pct} onChange={(v) => setI('mkt_game_pct', v)} suffix="%" />
              <RowNum label="Coste financiero" value={inputs.financiero_pct} onChange={(v) => setI('financiero_pct', v)} suffix="%" />
            </div>

            <Subtotal label="COSTE BÁSICO (sin extras)" value={result?.coste_basico_eur} />

            <div className="mt-2 grid gap-x-5 gap-y-1 lg:grid-cols-3">
              <RowNum
                label="Coste video"
                value={inputs.coste_video_eur}
                onChange={(v) => setI('coste_video_eur', v)}
                suffix="EUR"
                hint={!aplicaNewProduct ? 'Cliente sin coste vídeo por defecto — editable.' : undefined}
              />
              <RowNum
                label="Coste traducción"
                value={inputs.coste_traduccion_eur}
                onChange={(v) => setI('coste_traduccion_eur', v)}
                suffix="EUR"
                hint={!aplicaNewProduct ? 'Cliente sin coste traducción por defecto — editable.' : undefined}
              />
            </div>
            <Subtotal label="COSTE NEW PRODUCT (con video+traducción/ud)" value={result?.coste_new_product_eur} />

            <div className="mt-2 grid gap-x-5 gap-y-1 lg:grid-cols-3">
              <RowNum label="Provisiones" value={inputs.provisiones_eur} onChange={(v) => setI('provisiones_eur', v)} suffix="EUR" hint="Se suma" />
              <RowNum label="Bolsa" value={inputs.bolsa_eur} onChange={(v) => setI('bolsa_eur', v)} suffix="EUR" hint="Se resta" />
            </div>

            <Subtotal label="COSTE REAL" value={result?.coste_real_eur} bold />
          </Card>

          {/* ── Opciones (extras del fabricante) ── */}
          <Card title="Opciones (extras de fabricante)" description="Marca 'Incluir' para sumarlas al Coste fábrica.">
            <OpcionesEditor opciones={opciones} setOpciones={setOpciones} />
            <div className="mt-3 flex items-center justify-between rounded-md bg-slate-100 px-3 py-2 text-sm">
              <span className="text-xs uppercase tracking-wide text-slate-600">Total opciones incluidas</span>
              <span className="font-mono font-semibold">{fmt.usd(opcionesIncluidasTotal)}</span>
            </div>
          </Card>
        </div>

        {/* ── Columna derecha: paneles de coste + final + PVP ── */}
        <div className="space-y-3">
          <Card title="Resumen de costes">
            <div className="space-y-2">
              <PanelLine label="CDPe" value={result?.cdpe_eur} />
              <PanelLine label="Coste básico" value={result?.coste_basico_eur} />
              <PanelLine label="Coste new product" value={result?.coste_new_product_eur} />
              <PanelLine label="Coste real" value={result?.coste_real_eur} highlight />
            </div>

            <label className="mt-3 flex cursor-pointer items-start gap-2 rounded-md border border-slate-200 bg-white p-2 text-xs text-slate-700">
              <input
                type="checkbox"
                checked={garantizarMargen}
                onChange={(e) => setGarantizarMargen(e.target.checked)}
                className="mt-0.5 h-4 w-4 cursor-pointer"
              />
              <div className="flex-1">
                <span className="block font-semibold text-slate-800">Garantizar margen mínimo Approx ({fmt.pct(inputs.margen_approx_pct)})</span>
                <span className="block text-slate-500">
                  Si el coste final calculado dejaría a Approx con menos margen del estipulado, se sube el precio facturado lo necesario. Por defecto se copia de la condición comercial activa.
                </span>
                {precioMinGarantizado != null && garantizarMargen && precioMinGarantizado > (result?.coste_real_eur ?? 0) && (
                  <span className="mt-1 block text-emerald-700">
                    Precio mínimo aplicado: {fmt.eur(precioMinGarantizado)}
                    {' '}<span className="text-slate-400">(vs cálculo base {fmt.eur(result?.coste_real_eur ?? 0)})</span>
                  </span>
                )}
              </div>
            </label>

            <div className="mt-2 rounded-md bg-amber-50 px-3 py-2 ring-1 ring-amber-200">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600">
                Coste final (redondeable)
              </label>
              <div className="mt-1 flex items-center gap-2">
                <DecimalInput
                  value={costeFinal}
                  onChange={(v) => { setCosteFinalManual(true); setCosteFinal(v); }}
                  decimals={2}
                  ariaLabel="Coste final"
                  className="flex-1 text-right font-mono"
                />
                <span className="w-8 text-xs text-slate-500">EUR</span>
                {costeFinalManual && (
                  <button
                    type="button"
                    onClick={() => { setCosteFinalManual(false); if (result) setCosteFinal(Number(result.coste_real_eur.toFixed(2))); }}
                    className="cursor-pointer text-xs text-slate-500 hover:text-slate-700"
                  >
                    reset
                  </button>
                )}
              </div>
            </div>

            {(() => {
              // Margen real Approx: del precio facturado al cliente
              // (coste final) descontamos:
              //  - Rappel cliente + Comisión PM + Marketing (% sobre el
              //    precio facturado, devueltos al cliente / pagados al PM).
              //  - El incremento por vídeo + traducción que estos ya están
              //    imputados al precio facturado: Approx paga ese importe
              //    a un externo, no se queda con él.
              // No se descuenta el financiero (es coste interno de Approx).
              // Coste real para Approx = CDPe + provisiones/u − bolsa/u: el
              // dinero aprovisionado (a recuperar) es coste real para Approx y
              // la bolsa (saldo a favor del cliente) lo reduce; ambos por unidad.
              const descuentoPct = (inputs.rappel_game_pct + inputs.comision_pm_pct + inputs.mkt_game_pct) / 100;
              const descuentoPctEur = costeFinal * descuentoPct;
              const extrasUnidad = inputs.unidades > 0
                ? (inputs.coste_video_eur + inputs.coste_traduccion_eur) / inputs.unidades
                : 0;
              const ventaNeta = costeFinal - descuentoPctEur - extrasUnidad;
              const cdpe = result?.cdpe_eur ?? 0;
              const provUnidad = inputs.unidades > 0 ? inputs.provisiones_eur / inputs.unidades : 0;
              const bolsaUnidad = inputs.unidades > 0 ? inputs.bolsa_eur / inputs.unidades : 0;
              const costeRealApprox = cdpe + provUnidad - bolsaUnidad;
              const margenAbs = ventaNeta - costeRealApprox;
              // Margen comercial estándar: sobre la VENTA NETA (lo que
              // Approx efectivamente cobra al cliente, descontando lo que
              // se le devuelve por rappel/comisión PM/marketing y los
              // costes externos imputados por unidad).
              const margenPct = ventaNeta > 0 ? (margenAbs / ventaNeta) * 100 : null;
              const positivo = margenAbs >= 0;
              return (
                <div className={`mt-3 rounded-md px-3 py-2 ring-1 ${positivo ? 'bg-emerald-50 ring-emerald-300' : 'bg-rose-50 ring-rose-300'}`}>
                  <label
                    className={`block text-xs font-semibold uppercase tracking-wide ${positivo ? 'text-emerald-800' : 'text-rose-800'}`}
                    title="Margen sobre venta neta: margen € / venta neta × 100. Es decir, el margen sobre lo que Approx realmente cobra al cliente tras descontar rappel/comisión PM/marketing y los costes externos (vídeo/traducción)."
                  >
                    Margen real Approx (sobre venta neta)
                  </label>
                  <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
                    <span className="text-slate-600">Precio facturado</span>
                    <span className="text-right font-mono">{fmt.eur(costeFinal)}</span>
                    <span className="text-slate-600" title="Rappel cliente + Comisión PM + Marketing aplicados al precio facturado. El financiero NO se descuenta (coste interno).">
                      − descuentos % ({fmt.pct(descuentoPct * 100)})
                    </span>
                    <span className="text-right font-mono text-slate-600">−{fmt.eur(descuentoPctEur)}</span>
                    {extrasUnidad > 0 && (
                      <>
                        <span className="text-slate-600" title="Coste de vídeo y traducción por unidad. Imputados al precio facturado pero pagados a un externo (no es rentabilidad de Approx).">
                          − vídeo+traducción/ud
                        </span>
                        <span className="text-right font-mono text-slate-600">−{fmt.eur(extrasUnidad)}</span>
                      </>
                    )}
                    <span className="text-slate-600">Venta neta</span>
                    <span className="text-right font-mono">{fmt.eur(ventaNeta)}</span>
                    <span className="text-slate-600">− CDPe</span>
                    <span className="text-right font-mono text-slate-600">−{fmt.eur(cdpe)}</span>
                    {provUnidad > 0 && (
                      <>
                        <span className="text-slate-600" title="Dinero aprovisionado por unidad (a recuperar). Es coste real para Approx, reduce el margen.">
                          − provisiones/ud
                        </span>
                        <span className="text-right font-mono text-slate-600">−{fmt.eur(provUnidad)}</span>
                      </>
                    )}
                    {bolsaUnidad > 0 && (
                      <>
                        <span className="text-slate-600" title="Bolsa (saldo a favor del cliente) por unidad. Reduce el coste real de Approx, aumenta el margen.">
                          + bolsa/ud
                        </span>
                        <span className="text-right font-mono text-slate-600">+{fmt.eur(bolsaUnidad)}</span>
                      </>
                    )}
                    <span className={`font-semibold ${positivo ? 'text-emerald-900' : 'text-rose-900'}`}>= Margen Approx</span>
                    <span className={`text-right font-mono font-bold ${positivo ? 'text-emerald-900' : 'text-rose-900'}`}>
                      {fmt.eur(margenAbs)} ({margenPct != null ? fmt.pct(margenPct) : '—'})
                    </span>
                  </div>
                </div>
              );
            })()}

            <div className="mt-3 rounded-md bg-slate-900 px-3 py-3 text-white">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-300">
                PVPr con IVA (objetivo de venta)
              </label>
              <div className="mt-1 flex items-center gap-2">
                <DecimalInput
                  value={pvprIva}
                  onChange={setPvprIva}
                  decimals={2}
                  ariaLabel="PVPr con IVA"
                  className="flex-1 text-right font-mono text-slate-900"
                />
                <span className="w-8 text-xs text-slate-400">EUR</span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-md bg-slate-800/40 px-2 py-1.5">
                  <span className="text-slate-300">PV s/ IVA</span>
                  <p className="font-mono text-sm">{pvprSinIva != null ? fmt.eur(pvprSinIva) : '—'}</p>
                </div>
                <div className={`rounded-md px-2 py-1.5 ${margenGame != null ? 'bg-emerald-500/20 ring-1 ring-emerald-400/40' : 'bg-slate-800/40'}`}>
                  <span className={margenGame != null ? 'text-emerald-200' : 'text-slate-300'}>Margen cliente</span>
                  <p className={`font-mono text-lg font-bold tracking-tight ${margenGame != null ? 'text-emerald-300' : 'text-slate-400'}`}>
                    {margenGame != null ? fmt.pct(margenGame) : '—'}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <Button
                variant="create"
                onClick={() => saveCotiz.mutate()}
                disabled={!cabeceraValida || saveCotiz.isPending || !result}
              >
                {saveCotiz.isPending ? 'Guardando…' : '+ Añadir a cotizaciones'}
              </Button>
            </div>
            {!cabeceraValida && (
              <p className="mt-2 text-right text-xs text-rose-600">Categoría y Subcategoría son obligatorias.</p>
            )}
            {saveCotiz.isError && (
              <p className="mt-2 text-right text-xs text-rose-600">{(saveCotiz.error as Error).message}</p>
            )}
            {saveCotiz.isSuccess && fotoFile == null && (
              <p className="mt-2 text-right text-xs text-emerald-600">✓ Guardada.</p>
            )}
          </Card>

          {/* Tabla informativa de PVPr por margen */}
          <Card title="PVPr con IVA según margen" description={`IVA ${ivaPct}%. Calculado sobre Coste final.`}>
            {informativos.length === 0 ? (
              <p className="text-xs text-slate-400">Define el coste final para ver la tabla.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-2 py-1 text-left">Margen</th>
                    <th className="px-2 py-1 text-right">PVPr con IVA</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {informativos.map((r) => (
                    <tr key={r.margen}>
                      <td className="px-2 py-1 font-medium">{r.margen}%</td>
                      <td className="px-2 py-1 text-right font-mono">{fmt.eur(r.pv_iva)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>
      </div>

      {/* ── Listado de cotizaciones recientes (solo standalone) ── */}
      {!embebida && (
        <div className="mt-6">
          <Card title="Últimas cotizaciones" description="Histórico reciente generado desde esta calculadora.">
            {recientes.isLoading ? <Spinner /> : <CotizacionesRecientes rows={recientes.data?.data ?? []} />}
          </Card>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Subcomponentes
// ─────────────────────────────────────────────────────────────────────

function LabeledField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-600">{label}</label>
      {children}
    </div>
  );
}

/**
 * Fila de parámetro con etiqueta + input alineados.
 * Etiqueta a la izquierda, input a la derecha. Anchos fijos para que
 * todas las filas queden perfectamente alineadas verticalmente.
 *
 * El input es `DecimalInput`: acepta coma o punto como separador
 * decimal y los signos + y -. Para sufijos de moneda (EUR/USD)
 * redondea automáticamente a 2 decimales al perder el foco; para
 * "ud" rellena a 0 decimales; en % no se auto-formatea.
 */
function RowNum({ label, value, onChange, suffix, hint, readOnly }: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  suffix?: string;
  hint?: string;
  readOnly?: boolean;
}) {
  // Norma del holding: 2 decimales en TODA la app (incl. %, cambio FX, etc.).
  // Solo las unidades van sin decimales.
  const decimals = suffix === 'ud' ? 0 : 2;
  return (
    <div className="flex items-center gap-2 py-1">
      <div className="min-w-0 flex-1">
        <span className="block truncate text-sm text-slate-700" title={label}>{label}</span>
        {hint && <p className="truncate text-[10px] leading-tight text-slate-400" title={hint}>{hint}</p>}
      </div>
      <DecimalInput
        value={value}
        onChange={onChange}
        decimals={decimals}
        readOnly={readOnly}
        ariaLabel={label}
        style={{ width: '6rem' }}
        className="shrink-0 text-right font-mono text-sm"
      />
      <span style={{ width: '2.25rem' }} className="shrink-0 text-xs text-slate-500">{suffix ?? ''}</span>
    </div>
  );
}

function Subtotal({ label, value, bold }: { label: string; value: number | undefined; bold?: boolean }) {
  return (
    <div className={`mt-2 flex items-center justify-between rounded-md bg-slate-100 px-3 py-1.5 text-sm ${bold ? 'font-bold' : 'font-medium'}`}>
      <span className="uppercase tracking-wide text-xs text-slate-600">{label}</span>
      <span className="font-mono">{value != null ? fmt.eur(value) : '—'}</span>
    </div>
  );
}

function PanelLine({ label, value, highlight }: { label: string; value: number | undefined; highlight?: boolean }) {
  return (
    <div className={`flex items-center justify-between rounded-md px-3 py-1.5 text-sm ${highlight ? 'bg-slate-900 text-white' : 'bg-slate-50'}`}>
      <span className={`text-xs uppercase tracking-wide ${highlight ? 'text-slate-300' : 'text-slate-500'}`}>{label}</span>
      <span className="font-mono">{value != null ? fmt.eur(value) : '—'}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Editor de Opciones
// ─────────────────────────────────────────────────────────────────────

function OpcionesEditor({ opciones, setOpciones }: { opciones: Opcion[]; setOpciones: (o: Opcion[]) => void }) {
  const setOpcion = (idx: number, patch: Partial<Opcion>) => {
    setOpciones(opciones.map((o, i) => (i === idx ? { ...o, ...patch } : o)));
  };
  const remove = (idx: number) => setOpciones(opciones.filter((_, i) => i !== idx));
  const add = () => setOpciones([...opciones, opcionVacia()]);

  return (
    <div>
      <div className="space-y-2">
        {opciones.map((o, idx) => (
          <div key={o.uid} className="flex items-center gap-2">
            <span style={{ width: '2.75rem' }} className="shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-500">
              OP{idx + 1}
            </span>
            <div className="min-w-0 flex-1">
              <Input
                value={o.descripcion}
                onChange={(e) => setOpcion(idx, { descripcion: e.target.value })}
                placeholder="Descripción"
                className="text-sm"
              />
            </div>
            <DecimalInput
              value={o.importe_usd}
              onChange={(v) => setOpcion(idx, { importe_usd: v })}
              decimals={2}
              ariaLabel={`Importe OP${idx + 1}`}
              style={{ width: '6rem' }}
              className="shrink-0 text-right font-mono text-sm"
              placeholder="0,00"
            />
            <span style={{ width: '2.25rem' }} className="shrink-0 text-xs text-slate-500">USD</span>
            <label className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50">
              <input
                type="checkbox"
                checked={o.incluido}
                onChange={(e) => setOpcion(idx, { incluido: e.target.checked })}
                className="cursor-pointer"
              />
              Incluir
            </label>
            <button
              type="button"
              onClick={() => remove(idx)}
              disabled={opciones.length <= 1}
              className="shrink-0 cursor-pointer text-rose-600 hover:text-rose-800 disabled:cursor-not-allowed disabled:opacity-30"
              title="Eliminar fila"
            >
              ×
            </button>
          </div>
        ))}
      </div>
      <div className="mt-2">
        <Button size="sm" variant="create" onClick={add}>+ Añadir otra fila</Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Cotizaciones recientes
// ─────────────────────────────────────────────────────────────────────

type CotizacionRow = {
  id: string;
  fecha: string;
  unidades: number | null;
  coste_usd: number | string | null;
  cambio_usd_eur: number | string | null;
  cdpe_eur: number | string | null;
  coste_real_eur: number | string | null;
  coste_game_final_eur: number | string | null;
  pvpr_iva_eur: number | string | null;
  ref_proveedor: string | null;
  nombre_propuesto: string | null;
  subcategoria_nombre: string | null;
  categoria_nombre: string | null;
  proveedor_nombre: string | null;
  producto_sku: string | null;
  producto_nombre: string | null;
  notas: string | null;
};

function CotizacionesRecientes({ rows }: { rows: CotizacionRow[] }) {
  if (rows.length === 0) {
    return <p className="text-xs text-slate-400">Aún no hay cotizaciones guardadas.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
          <tr>
            <th className="px-2 py-1.5 text-left">Fecha</th>
            <th className="px-2 py-1.5 text-left">Producto / ref</th>
            <th className="px-2 py-1.5 text-left">Categoría</th>
            <th className="px-2 py-1.5 text-left">Subcategoría</th>
            <th className="px-2 py-1.5 text-left">Proveedor</th>
            <th className="px-2 py-1.5 text-right">Uds.</th>
            <th className="px-2 py-1.5 text-right">Coste USD</th>
            <th className="px-2 py-1.5 text-right">CDPe</th>
            <th className="px-2 py-1.5 text-right">Real</th>
            <th className="px-2 py-1.5 text-right">Final</th>
            <th className="px-2 py-1.5 text-right">PVPr IVA</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-slate-50">
              <td className="whitespace-nowrap px-2 py-1 text-xs text-slate-500">{fmt.datetime(r.fecha)}</td>
              <td className="px-2 py-1 text-sm">
                {r.producto_sku
                  ? <span className="font-mono text-xs">{r.producto_sku}</span>
                  : (r.nombre_propuesto || r.ref_proveedor || <span className="italic text-slate-400">—</span>)}
              </td>
              <td className="px-2 py-1 text-xs">{r.categoria_nombre ?? '—'}</td>
              <td className="px-2 py-1 text-xs">{r.subcategoria_nombre ?? '—'}</td>
              <td className="px-2 py-1 text-xs">{r.proveedor_nombre ?? '—'}</td>
              <td className="px-2 py-1 text-right font-mono text-xs">{r.unidades != null ? fmt.int(r.unidades) : '—'}</td>
              <td className="px-2 py-1 text-right font-mono text-xs">{fmt.usd(r.coste_usd)}</td>
              <td className="px-2 py-1 text-right font-mono text-xs">{fmt.eur(r.cdpe_eur)}</td>
              <td className="px-2 py-1 text-right font-mono text-xs">{fmt.eur(r.coste_real_eur)}</td>
              <td className="px-2 py-1 text-right font-mono text-xs font-semibold">{fmt.eur(r.coste_game_final_eur)}</td>
              <td className="px-2 py-1 text-right font-mono text-xs font-semibold">{fmt.eur(r.pvpr_iva_eur)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
